import { Box, Icon, Skeleton, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { AgentPriceCard as AgentPriceCardType, Components } from '@/schemas/sceneDataSchema';
import { assertNever } from '@/utils/assertNever';
import useModal from '@/hooks/use-modals';
import {
  TypographyAlign,
  TypographyColors,
  TypographyFontWeight,
  TypographyVariants,
} from '@/constants/typographyTypes';
import NumberFlow from '@number-flow/react';
import { LinkTypes } from '@/constants/linkTypes';
import { ModalLink } from './formElements/typographies/ModalLink';
import { StyledChip as TermChip } from './formElements/selects/BaseChipElement';
import { ModalDialog } from './ModalDialog';
import { ButtonSizes, ButtonColors, ButtonVariants, ButtonTypes } from '@/constants/buttonTypes';
import { DynamicRenderer } from './DynamicRenderer';
import { ModalButton } from './formElements/buttons/ModalButton';
import { PricingTerm } from '@/constants/pricingTypes';
import useAnalytics, { eventName } from '@/hooks/use-analytics';
import { useQuote, useUpdatePricingTerm } from '@/hooks/use-quote';
import { usePricePolling } from '@/hooks/quote/use-price-polling';

// Top level state
// Passed into each respective card component
interface EstimatePriceCardState {
  state: 'estimate';
  openPriceEstimateModal: () => void;
  priceInDollars: number | undefined;
  dueTodayAmountInDollars: number | undefined;
  paymentDetailsModalId: string;
  paymentModalContent: Components | undefined;
  shouldShowTermSelector: boolean;
  termSelection: PricingTerm;
  onChangeTermSelection: (newTerm: PricingTerm) => void;
}

interface BindablePriceCardState {
  state: 'bindable';
  openPriceEstimateModal: () => void;
  priceInDollars: number | undefined;
  dueTodayAmountInDollars: number | undefined;
  paymentDetailsModalId: string;
  paymentModalContent: Components | undefined;
  shouldShowTermSelector: boolean;
  termSelection: PricingTerm;
  onChangeTermSelection: (newTerm: PricingTerm) => void;
}

type PriceCardState = EstimatePriceCardState | BindablePriceCardState;

// Top Level Component. Responsible for mapping data, rendering the appropriate price card based on state, and callback logic
export const AgentPriceCard = ({ component }: { component: AgentPriceCardType }) => {
  // hooks
  const { getModal } = useModal();
  const { trackEvent } = useAnalytics();
  const updatePricingTerm = useUpdatePricingTerm();
  const quote = useQuote();
  const priceEstimateModal = getModal(component.price_estimate_modal_id ?? '');
  usePricePolling();

  // handlers
  const onChangeTermSelection = (term: PricingTerm) => {
    trackEvent(`${eventName(term)}_SELECTED`);
    void updatePricingTerm(term);
  };

  // derived state
  const quoteKind = quote?.details.quote_kind ?? '';
  const termSelection: PricingTerm = quote?.details.initial_term_selection ?? PricingTerm.FULL_TERM;
  const shouldShowTermSelector = !window.location.pathname.includes('checkout');
  const paymentModalContent = quote?.payment_modal_content ?? [];

  const priceInDollars: number | undefined = quote
    ? termSelection === PricingTerm.MONTHLY
      ? quote.details.monthly_in_cents / 100
      : quote.details.full_term_in_cents / 100
    : undefined;

  const dueTodayAmountInDollars: number | undefined = quote
    ? termSelection === PricingTerm.MONTHLY
      ? quote.details.due_today_monthly_in_dollars
      : quote.details.due_today_full_term_in_dollars
    : undefined;

  const priceCardState: PriceCardState = (() => {
    if (quoteKind === 'bindable_quote' || quoteKind === 'finalized_quote') {
      return {
        state: 'bindable',
        openPriceEstimateModal: () => priceEstimateModal?.open(),
        priceInDollars,
        dueTodayAmountInDollars,
        paymentDetailsModalId: component.payment_details_modal_id ?? '',
        paymentModalContent,
        shouldShowTermSelector,
        termSelection,
        onChangeTermSelection,
      };
    }

    return {
      state: 'estimate',
      openPriceEstimateModal: () => priceEstimateModal?.open(),
      priceInDollars,
      dueTodayAmountInDollars,
      paymentDetailsModalId: component.payment_details_modal_id ?? '',
      paymentModalContent,
      shouldShowTermSelector,
      termSelection,
      onChangeTermSelection,
    };
  })();

  // render
  // i.e. return the appropriate card based on the state
  // wrapped in an IIFE to accommodate ts' built-in exhaustive pattern matching
  return ((state: PriceCardState) => {
    switch (state.state) {
      case 'estimate':
        return (
          <EstimatePriceCard
            key='estimate'
            {...state}
          />
        );
      case 'bindable':
        return (
          <BindablePriceCard
            key='bindable'
            {...state}
          />
        );
      default:
        return assertNever(state);
    }
  })(priceCardState);
};

// Individual Price Card Components
const EstimatePriceCard = (state: EstimatePriceCardState) => (
  <StyledFadeStack spacing={0}>
    <PriceEstimateTitle
      text='Price Estimate'
      openPriceEstimateModal={state.openPriceEstimateModal}
    />
    <PriceWhenAvailable
      priceInDollars={state.priceInDollars}
      termSelection={state.termSelection}
    />
    <Subtitle text='Add as many details as you can for the most accurate price estimate.' />
    <DueTodayRow
      dueTodayAmountInDollars={state.dueTodayAmountInDollars}
      paymentDetailsModalId={state.paymentDetailsModalId}
    />
    {state.shouldShowTermSelector && <TermSelector {...state} />}
    {state.paymentModalContent && (
      <PaymentDetailsModal
        paymentDetailsModalId={state.paymentDetailsModalId}
        paymentModalContent={state.paymentModalContent}
      />
    )}
  </StyledFadeStack>
);

const BindablePriceCard = (state: BindablePriceCardState) => (
  <StyledFadeStack spacing={0}>
    <PriceEstimateTitle
      text='Bindable price'
      openPriceEstimateModal={state.openPriceEstimateModal}
    />
    <PriceWhenAvailable
      priceInDollars={state.priceInDollars}
      termSelection={state.termSelection}
    />
    <Subtitle text='Add as many details as you can for the most accurate price estimate.' />
    <DueTodayRow
      dueTodayAmountInDollars={state.dueTodayAmountInDollars}
      paymentDetailsModalId={state.paymentDetailsModalId}
    />
    {state.shouldShowTermSelector && <TermSelector {...state} />}
    {state.paymentModalContent && (
      <PaymentDetailsModal
        paymentDetailsModalId={state.paymentDetailsModalId}
        paymentModalContent={state.paymentModalContent}
      />
    )}
  </StyledFadeStack>
);

// Sub Components used in the Price Cards, alphabetized
const DueTodayRow = ({
  dueTodayAmountInDollars,
  paymentDetailsModalId,
}: {
  dueTodayAmountInDollars?: number;
  paymentDetailsModalId: string;
}) => {
  if (dueTodayAmountInDollars) {
    return (
      <StyledDueTodayRow>
        <Typography variant='h4'>
          <NumberFlow
            value={dueTodayAmountInDollars}
            format={{ style: 'currency', currency: 'USD' }}
            suffix=' Due today'
            spinTiming={{ duration: 750, easing: 'ease-in-out' }}
            opacityTiming={{ duration: 350, easing: 'ease-in-out' }}
            willChange
          />
        </Typography>
        <ModalLink
          component={{
            type: LinkTypes.MODAL,
            text: 'See details',
            method_type: 'open',
            modal_id: paymentDetailsModalId,
            color: TypographyColors.TEXT_PRIMARY,
            align: TypographyAlign.LEFT,
            variant: TypographyVariants.BODY2,
            font_weight: TypographyFontWeight.REGULAR,
          }}
        />
      </StyledDueTodayRow>
    );
  } else {
    return (
      <StyledDueTodayRow>
        <Skeleton
          variant='rounded'
          width='100%'
          height={27}
          animation='wave'
        />
      </StyledDueTodayRow>
    );
  }
};

const PaymentDetailsModal = ({
  paymentDetailsModalId,
  paymentModalContent,
}: {
  paymentDetailsModalId: string;
  paymentModalContent: Components;
}) => (
  <ModalDialog
    children={
      <DynamicRenderer
        components={paymentModalContent}
        route={async () => {}}
      />
    }
    id={paymentDetailsModalId}
    status='closed'
    footerChildren={
      <ModalButton
        component={{
          id: `payment_details_modal_button_close`,
          label: 'Close',
          size: ButtonSizes.LARGE,
          color: ButtonColors.PRIMARY,
          start_icon: null,
          end_icon: null,
          variant: ButtonVariants.CONTAINED,
          type: ButtonTypes.MODAL,
          modal_id: paymentDetailsModalId,
          method_type: 'close',
        }}
      />
    }
  />
);

const PriceEstimateTitle = ({ text, openPriceEstimateModal }: { text: string; openPriceEstimateModal: () => void }) => (
  <StyledClickableBox onClick={openPriceEstimateModal}>
    <Typography variant='body2'>{text}</Typography>
    <StyledIcon>info_outline</StyledIcon>
  </StyledClickableBox>
);

const PriceWhenAvailable = ({
  priceInDollars,
  termSelection,
}: {
  priceInDollars?: number;
  termSelection: PricingTerm;
}) => {
  if (!priceInDollars) {
    return (
      <Typography
        variant='body1'
        color='textSecondary'
      >
        Loading price...
      </Typography>
    );
  }

  return (
    <StyledPrice variant={'h1'}>
      <NumberFlow
        value={priceInDollars}
        format={{ style: 'currency', currency: 'USD' }}
        suffix={termSelection === PricingTerm.MONTHLY ? '/mo' : '/6 mo'}
        spinTiming={{
          duration: 750,
          easing: 'ease-in-out',
        }}
        opacityTiming={{
          duration: 350,
          easing: 'ease-in-out',
        }}
        willChange={true}
      />
    </StyledPrice>
  );
};

const Subtitle = ({ text }: { text: string }) => (
  <Typography
    variant='body2'
    color='textSecondary'
  >
    {text}
  </Typography>
);

const TermSelector = ({
  termSelection,
  onChangeTermSelection,
}: {
  termSelection: PricingTerm;
  onChangeTermSelection: (newTerm: PricingTerm) => void;
}) => (
  <StyledChipContainer>
    {Object.values(PricingTerm).map((term) => (
      <TermChip
        key={term}
        label={term === PricingTerm.MONTHLY ? 'Monthly' : '6 months'}
        icon={termSelection === term ? <StyledCheckIcon color='primary'>check</StyledCheckIcon> : undefined}
        selected={termSelection === term}
        size='small'
        variant='outlined'
        onClick={() => onChangeTermSelection(term)}
      />
    ))}
  </StyledChipContainer>
);

// Styled Components, alphabetized
const StyledCheckIcon = styled(Icon)`
  & > .MuiIcon-root {
    height: 24px;
    width: 24px;
  }
`;

const StyledChipContainer = styled('div')`
  display: flex;
  align-items: center;
  align-content: center;
  gap: 8px var(--3, 8px);
  align-self: stretch;
  flex-wrap: none;
`;

const StyledClickableBox = styled(Box)`
  cursor: pointer;
  user-select: none;

  display: flex;
`;

const StyledDueTodayRow = styled('div')`
  display: flex;
  align-items: center;
  gap: var(--3, 8px);
  align-self: stretch;
  margin-top: var(--5, 16px);

  &:not(:last-child) {
    margin-bottom: var(--6, 20px);
  }
`;

const StyledFadeStack = styled(Stack)`
  animation: fadeIn 0.2s;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const StyledIcon = styled(Icon)`
  font-family: 'Material Symbols Outlined';
  font-size: 12px;
  margin-left: 2px;
  align-self: center;
`;

const StyledPrice = styled(Typography)`
  & number-flow-react::part(suffix) {
    font-size: 0.6em;
  }
`;
